import { useMemo } from 'react';
import type { PlanCell, StructuralObject } from '../../../types/plan';
import { planCellKey } from '../../../utils/planCells';

export interface GreenhouseGroup {
  /** Structural object UUID */
  id: string;
  /** Structural object kind */
  kind: string;
  /** Structural object subtype (greenhouse, parnik, geotextile, etc.) */
  subtype: string;
  /** All cell keys in this structural object */
  cellKeys: Set<string>;
  /** Bounding rect in grid coords */
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

/**
 * Group cells by structural object ID.
 * No flood-fill needed — each cell carries its structuralObjectId.
 */
export const useGreenhouseGroups = (
  cellMap: Map<string, PlanCell>,
  structuralObjects?: StructuralObject[],
): GreenhouseGroup[] => {
  return useMemo(() => {
    // Build groups by structuralObjectId
    const groupMap = new Map<string, {
      cellKeys: Set<string>;
      minRow: number;
      maxRow: number;
      minCol: number;
      maxCol: number;
    }>();

    cellMap.forEach((cell, key) => {
      if (!cell.structuralObjectId) return;
      const soId = cell.structuralObjectId;

      let group = groupMap.get(soId);
      if (!group) {
        group = {
          cellKeys: new Set(),
          minRow: Infinity,
          maxRow: -Infinity,
          minCol: Infinity,
          maxCol: -Infinity,
        };
        groupMap.set(soId, group);
      }

      group.cellKeys.add(key);
      group.minRow = Math.min(group.minRow, cell.row);
      group.maxRow = Math.max(group.maxRow, cell.row);
      group.minCol = Math.min(group.minCol, cell.col);
      group.maxCol = Math.max(group.maxCol, cell.col);
    });

    if (groupMap.size === 0) return [];

    // Build SO lookup map
    const soLookup = new Map<string, StructuralObject>();
    if (structuralObjects) {
      for (const so of structuralObjects) {
        soLookup.set(so.id, so);
      }
    }

    const groups: GreenhouseGroup[] = [];
    groupMap.forEach((data, soId) => {
      const so = soLookup.get(soId);
      if (so && so.kind !== 'covering') return;
      groups.push({
        id: soId,
        kind: so?.kind ?? 'covering',
        subtype: so?.subtype ?? 'greenhouse',
        ...data,
      });
    });

    return groups;
  }, [cellMap, structuralObjects]);
};

/**
 * Compute contour borders for a greenhouse group.
 * Returns Map<cellKey, {top, right, bottom, left}> where true = outer edge.
 */
export const computeGreenhouseContour = (
  cellKeys: Set<string>,
): Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }> => {
  const borders = new Map<string, { top: boolean; right: boolean; bottom: boolean; left: boolean }>();

  cellKeys.forEach((key) => {
    const [rowStr, colStr] = key.split(':');
    const row = parseInt(rowStr, 10);
    const col = parseInt(colStr, 10);

    const topRow = row === 1 ? -1 : row - 1;
    const bottomRow = row === -1 ? 1 : row + 1;
    const leftCol = col === 1 ? -1 : col - 1;
    const rightCol = col === -1 ? 1 : col + 1;

    borders.set(key, {
      top: !cellKeys.has(planCellKey(topRow, col)),
      bottom: !cellKeys.has(planCellKey(bottomRow, col)),
      left: !cellKeys.has(planCellKey(row, leftCol)),
      right: !cellKeys.has(planCellKey(row, rightCol)),
    });
  });

  return borders;
};
